<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h2>{{ $tt('customRefLine') }}</h2>
      </v-col>
      <v-spacer />
      <v-col align="end" justify="end" class="pa-0 pt-6">
        <export-dialog :exportMode="exportMode" />
      </v-col>
    </v-row>

    <div class="pa-2 " v-if="refLines.length > 0">
      <div v-resize="onResize">
        <div v-if="!isMobile">
          <v-simple-table>
            <tbody>
              <tr v-for="refLine in refLines" :key="refLine.id">
                <td width="36">
                  <user-icon-component
                    size="36"
                    :showNickname="true"
                    :iconUrl="getAccount(refLine).iconUrl"
                    :nickname="getAccount(refLine).nickname"
                  />
                </td>
                <td>
                  {{ refLine.createdDateTime | aidToYearMonthDayHourMin }}
                </td>
                <td>
                  <v-list style="background: none" two-line>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ getFieldNameFromFieldGeoInfoKeyFromRefLine(refLine) }}
                        </v-list-item-title>
                        <v-list-item-title>
                          {{ getRefLineName(refLine) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </td>
                <td>
                  {{ getTypeAsString(refLine) }}
                </td>
                <td>
                  <v-list style="background: none" two-line>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $tt('customGuidanceInterval') }} {{ getGuidanceIntervalAsString(refLine) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </td>
                <td>
                  <v-list style="background: none" two-line>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $tt('customRefLineLength') }} {{ getLineLengthAsString(refLine) }}
                        </v-list-item-title>
                        <v-list-item-title>
                          {{ $tt('customDirecton') }} {{ getDirectionAsString(refLine) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </td>
                <td>
                  <v-btn color="primary mr-2" @click="showDetail(refLine)">{{ $tt('customDetail') }}</v-btn>
                  <v-btn color="error" :disabled="!isMyRefLine(refLine)" @click="showDeleteDialog(refLine)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
        <div v-else>
          <v-hover v-slot:default="{ hover }" v-for="refLine in refLines" :key="refLine.id">
            <v-card :elevation="hover ? 12 : 2" color="mb-4">
              <v-list>
                <v-list-item three-line>
                  <v-list-item-avatar>
                    <user-icon-component
                      size="36"
                      :showNickname="true"
                      :iconUrl="getAccount(refLine).iconUrl"
                      :nickname="getAccount(refLine).nickname"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ refLine.lastModifiedDateTime | aidToYearMonthDayHourMin }}</v-list-item-title>
                    <v-list-item-title>
                      {{ getFieldNameFromFieldGeoInfoKeyFromRefLine(refLine) }}
                    </v-list-item-title>
                    <v-list-item-title>
                      {{ getRefLineName(refLine) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="pa-1">
                      {{ getTypeAsString(refLine) }}
                    </v-list-item-title>
                    <v-list-item-title class="pa-1">
                      {{ $tt('customGuidanceInterval') }} {{ getGuidanceIntervalAsString(refLine) }}
                    </v-list-item-title>
                    <v-list-item-title class="pa-1">
                      {{ $tt('customRefLineLength') }} {{ getLineLengthAsString(refLine) }}
                    </v-list-item-title>
                    <v-list-item-title class="pa-1">
                      {{ $tt('customDirecton') }} {{ getDirectionAsString(refLine) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-card-actions>
                <div class="flex-grow-1"></div>
                <v-btn color="primary mr-2" @click="showDetail(refLine)">{{ $tt('customDetail') }}</v-btn>
                <v-btn color="error" :disabled="!isMyRefLine(refLine)" @click="showDeleteDialog(refLine)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-hover>
        </div>
      </div>
    </div>
    <v-row v-else justify="center" align-content="center">
      <v-col cols="8" class="pt-12">
        <v-img class="img-fluid" src="/images/reflines_list_empty.png" />
      </v-col>
    </v-row>
    <pagination-component
      :pagination-info="paginationInfo"
      :paging-type="pagingType"
      @update-paging="updatePaging($event)"
    />
    <v-dialog v-model="isShowDeleteDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ deleteRefLineMessage }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeDeleteDialog()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="deleteRefLine()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <now-loading-overlay-component :nowLoading="nowLoading" />
  </v-container>
</template>

<script>
import ExportDialog from '@/components/export/ExportDialog'
import PaginationComponent from '@/components/PaginationComponent'
import FieldNameMixin from '@/components/mixins/field-name-mixin'
import RefLineMixin from '@/components/mixins/ref-line-mixin'
import UserIconComponent from '@/components/user/UserIconComponent'
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import * as Constants from '@/lib/constants'
import PagingOptions from '@/lib/paging-options'
import i18n from '@/lib/i18n'

export default {
  name: 'RefLineListAsList',
  mixins: [FieldNameMixin, RefLineMixin],
  components: { ExportDialog, UserIconComponent, PaginationComponent, NowLoadingOverlayComponent },
  data() {
    return {
      exportMode: Constants.EXPORT_MODE.REFLINES,
      isMobile: false,
      pagingType: PagingOptions.REF_LINE_PAGING_TYPE,
      deleteRefLineMessage: null,
      isShowDeleteDialog: false,
      refLineForDelete: null,
      nowLoading: false,
    }
  },
  mounted() {
    this.checkAvailabilityBeforeMount()
    this.initRefLines()
  },
  methods: {
    closeDeleteDialog() {
      this.refLineForDelete = null
      this.isShowDeleteDialog = false
    },
    deleteRefLine() {
      if (this.refLineForDelete) {
        const request = {
          refLine: this.refLineForDelete,
          onSuccess: this.deleteRefLineSuccess,
          onFailure: this.deleteRefLineFailure,
        }
        this.openNowLoading()
        this.$store.dispatch('deleteRefLine', request)
      }
    },
    deleteRefLineSuccess() {
      this.closeDeleteDialog()
      this.closeNowLoading()
      this.$store.dispatch('initRefLines')
    },
    deleteRefLineFailure() {
      this.closeDeleteDialog()
      this.closeNowLoading()
    },
    openNowLoading() {
      this.nowLoading = true
    },
    closeNowLoading() {
      this.nowLoading = false
    },
    showDeleteDialog(refLine) {
      this.refLineForDelete = refLine
      let refLineName = this.getRefLineName(refLine)
      refLineName = i18n.tt('customRefLineDeleteKnownName', { refLineName })
      if (!refLine.name) {
        refLineName = i18n.tt('customRefLineDeleteUnknownName')
      }
      this.deleteRefLineMessage = i18n.tt('customRefLineDeleteAlert', { refLineName })
      this.isShowDeleteDialog = true
    },
    onResize() {
      if (window.innerWidth < Constants.RESPONSIVE_BREAKPOINT_TASK_LIST) this.isMobile = true
      else this.isMobile = false
    },
    initRefLines() {
      this.$store.dispatch('initRefLines')
    },
    updatePaging(pagingOptions) {
      this.$store.dispatch('updatePagingOfRefLinesList', { pagingOptions })
    },
    showDetail(refLine) {
      this.$store.dispatch('showRefLineDetail', refLine)
    },
  },
  computed: {
    refLines() {
      return this.$store.getters.refLines
    },
    paginationInfo() {
      return this.$store.getters.paginationInfos.refLines
    },
  },
}
</script>
